'use client'

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useQueryClient } from '@tanstack/react-query'
import type { User } from '@skop-app/lib-domain/types'

import {
  emitCookieChange,
  readClientCookie,
} from '@/utils/cookies/clientCookies'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { userKeys } from '@/services/user/user.keys'
import { useUser } from '@/services/user/user.query'
import { pref, type Prefs, setPref } from '@/utils/pref'
import { updateUserStrict } from '@/services/user/user.mutation'
import {
  clearSelectedTeamCookie,
  getSelectedTeamSessionScopeKey,
  setSelectedTeamCookieForSession,
} from '@/app/actions/team'
import { SELECTED_TEAM_COOKIE_NAME } from '@/features/team-selection/constants'
import {
  getPersonalWorkspaceMembership,
  resolveActiveTeamId,
} from '@/features/team-selection/lib/resolveActiveTeamId'
import { runWithSelectedTeamSessionLock } from '@/features/team-selection/lib/selectionLock'
import { selectedTeamCookieStore } from '@/features/team-selection/lib/selectedTeamCookieStore'

const parseSelected = (
  raw: string | null | undefined,
): { isSolo: true; id: undefined } | { isSolo: false; id?: string } => {
  if (!raw) return { isSolo: false }
  if (raw === 'solo') return { isSolo: true, id: undefined }
  return { isSolo: false, id: raw }
}

export function useSelectedTeam() {
  const queryClient = useQueryClient()
  const raw = useSyncExternalStore(
    selectedTeamCookieStore.subscribe,
    selectedTeamCookieStore.getSnapshot,
    selectedTeamCookieStore.getServerSnapshot,
  )
  const { data: userData, refetch: refetchUser } = useUser()
  const user = userData?.user
  const teams = userData?.organizations
  const preferredTeamId = pref(user?.preferences, 'user-default-team-id')
  const {
    currentUserId,
    impersonatedUserId,
    isImpersonating,
    sessionScopeKey,
  } = useSessionRuntime()

  // Track initialization to avoid persisting preferences during first resolution
  const initializedRef = useRef(false)

  const { isSolo, id } = useMemo(() => parseSelected(raw), [raw])
  const resolvedTeamId = useMemo(() => {
    if (!teams) return id ?? null
    return resolveActiveTeamId(teams, raw, preferredTeamId)
  }, [id, preferredTeamId, raw, teams])
  const resolvedIsSolo = useMemo(() => {
    if (!teams) return isSolo
    return resolvedTeamId === null
  }, [isSolo, resolvedTeamId, teams])

  const currentUserQueryKey = useMemo(
    () =>
      currentUserId
        ? userKeys.currentScoped({
            sessionScopeKey,
            currentUserId,
            impersonatedUserId,
          })
        : userKeys.current(),
    [currentUserId, impersonatedUserId, sessionScopeKey],
  )

  const cacheCurrentUserPreferences = useCallback(
    (preferences: Prefs, fallbackData: User | null | undefined) => {
      queryClient.setQueryData<User | null>(
        currentUserQueryKey,
        (currentData) => {
          const source = currentData ?? fallbackData
          if (!source?.user) return source

          return {
            ...source,
            user: {
              ...source.user,
              preferences: preferences ?? [],
            },
          }
        },
      )
    },
    [currentUserQueryKey, queryClient],
  )

  const setSelected = useCallback(
    (
      teamId: string,
      options?: {
        skipPreferenceUpdate?: boolean
        forceScopeAlignment?: boolean
        cookieValue?: 'solo'
      },
    ): Promise<boolean> => {
      const select = async (): Promise<boolean> => {
        let serverSessionScopeKey: string
        try {
          serverSessionScopeKey = await getSelectedTeamSessionScopeKey()
        } catch {
          return false
        }
        if (serverSessionScopeKey !== sessionScopeKey) {
          return false
        }
        const value = teamId
        const selectedCookieValue = options?.cookieValue ?? value
        const previousCookie = readClientCookie(SELECTED_TEAM_COOKIE_NAME)
        // Idempotence: no-op if selection is unchanged
        if (
          previousCookie === selectedCookieValue &&
          !options?.forceScopeAlignment
        )
          return true

        const selectedUserId = user?.id
        const shouldAlignAuthorizer =
          !isImpersonating &&
          !options?.skipPreferenceUpdate &&
          Boolean(selectedUserId)
        let previousPreferences: Prefs
        let previousTeamId: string | undefined
        let freshUserData: User | null | undefined
        let hasFreshUser = false
        let preferenceUpdated = false
        let authorizerAligned = false
        let cookieUpdated = false

        const refreshAuthorizer = async () => {
          const session = await fetchAuthSession({ forceRefresh: true })
          if (!session.tokens?.idToken) {
            throw new Error('Refreshed session is missing an ID token')
          }
        }

        const persistPreferences = async (
          preferences: Prefs,
          { updateCache = true }: { updateCache?: boolean } = {},
        ) => {
          if (!selectedUserId) return

          await updateUserStrict({
            id: selectedUserId,
            preferences: preferences ?? [],
          })
          if (updateCache) {
            cacheCurrentUserPreferences(preferences, freshUserData)
          }
        }

        const restoreTeamPreference = async (
          teamId: string | undefined,
        ): Promise<void> => {
          const latestUserResult = await refetchUser({ throwOnError: true })
          const latestUser = latestUserResult.data?.user
          if (!latestUser?.id || latestUser.id !== selectedUserId) {
            throw new Error('Session scope changed during team rollback')
          }

          const latestPreferences = latestUser.preferences
          const rollbackPreferences = teamId
            ? setPref(latestPreferences, 'user-default-team-id', teamId)
            : latestPreferences?.filter(
                (preference) => preference.key !== 'user-default-team-id',
              )
          freshUserData = latestUserResult.data
          await persistPreferences(rollbackPreferences)
        }

        try {
          if (shouldAlignAuthorizer && selectedUserId) {
            const freshUserResult = await refetchUser({ throwOnError: true })
            const freshUser = freshUserResult.data?.user
            if (!freshUser?.id || freshUser.id !== selectedUserId) return false
            // A revoked workspace must not publish a solo UI cookie while the
            // authorizer still targets the previously preferred organization.
            if (
              options?.cookieValue === 'solo' &&
              getPersonalWorkspaceMembership(
                freshUserResult.data?.organizations,
              )?.value !== value
            ) {
              return false
            }
            freshUserData = freshUserResult.data
            hasFreshUser = true
            previousPreferences = freshUser.preferences

            const existingPref = pref(
              freshUser.preferences,
              'user-default-team-id',
            )
            previousTeamId =
              resolveActiveTeamId(
                freshUserResult.data?.organizations,
                previousCookie,
                existingPref,
              ) ?? undefined
            if (existingPref !== value) {
              await persistPreferences(
                setPref(freshUser.preferences, 'user-default-team-id', value),
              )
              preferenceUpdated = true
            }

            await refreshAuthorizer()
            authorizerAligned = true

            if (
              options?.forceScopeAlignment &&
              previousCookie === selectedCookieValue
            ) {
              const alignedUserResult = await refetchUser({
                throwOnError: true,
              })
              const alignedUser = alignedUserResult.data?.user
              if (!alignedUser?.id || alignedUser.id !== selectedUserId) {
                throw new Error('Session scope changed during team alignment')
              }
              freshUserData = alignedUserResult.data
            }
          }

          await setSelectedTeamCookieForSession(
            selectedCookieValue,
            sessionScopeKey,
          )
          cookieUpdated = true
          emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
        } catch {
          if (selectedUserId && hasFreshUser) {
            try {
              if (authorizerAligned) {
                const previousPreferenceTeamId = pref(
                  previousPreferences,
                  'user-default-team-id',
                )
                const needsScopeRepair = Boolean(
                  previousTeamId && previousTeamId !== previousPreferenceTeamId,
                )
                const rollbackPreferences =
                  needsScopeRepair && previousTeamId
                    ? setPref(
                        previousPreferences,
                        'user-default-team-id',
                        previousTeamId,
                      )
                    : previousPreferences

                if (preferenceUpdated || needsScopeRepair) {
                  await restoreTeamPreference(
                    pref(rollbackPreferences, 'user-default-team-id'),
                  )
                }

                await refreshAuthorizer()
              } else if (preferenceUpdated) {
                await restoreTeamPreference(
                  pref(previousPreferences, 'user-default-team-id'),
                )
              }
            } catch (rollbackError) {
              console.error(
                'Failed to restore the previous team selection scope',
                rollbackError,
              )
            }
          }

          if (cookieUpdated) {
            try {
              if (previousCookie) {
                await setSelectedTeamCookieForSession(
                  previousCookie,
                  sessionScopeKey,
                )
              } else {
                await clearSelectedTeamCookie(sessionScopeKey)
              }
              emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
            } catch (rollbackError) {
              console.error(
                'Failed to restore the previous team selection cookie',
                rollbackError,
              )
            }
          }
          return false
        }

        return true
      }

      return runWithSelectedTeamSessionLock(select)
    },
    [
      cacheCurrentUserPreferences,
      isImpersonating,
      refetchUser,
      sessionScopeKey,
      user,
    ],
  )

  const setSolo = useCallback((): Promise<boolean> => {
    const personalWorkspaceId = getPersonalWorkspaceMembership(teams)?.value
    if (personalWorkspaceId) {
      return setSelected(personalWorkspaceId, {
        cookieValue: 'solo',
        forceScopeAlignment: true,
      })
    }

    return runWithSelectedTeamSessionLock(async () => {
      let serverSessionScopeKey: string
      try {
        serverSessionScopeKey = await getSelectedTeamSessionScopeKey()
      } catch {
        return false
      }
      if (serverSessionScopeKey !== sessionScopeKey) return false
      if (readClientCookie(SELECTED_TEAM_COOKIE_NAME) === 'solo') return true

      try {
        await setSelectedTeamCookieForSession('solo', sessionScopeKey)
        emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
      } catch {
        return false
      }
      return true
    })
  }, [sessionScopeKey, setSelected, teams])

  const clear = useCallback(async () => {
    let persisted = false
    try {
      await clearSelectedTeamCookie(sessionScopeKey)
      emitCookieChange(SELECTED_TEAM_COOKIE_NAME)
      persisted = true
    } catch {}
    if (!persisted) return
  }, [sessionScopeKey])

  // Initialize team selection when user data is available
  useEffect(() => {
    let cancelled = false

    const initialize = async () => {
      // Skip if already initialized
      if (initializedRef.current) return
      // The server snapshot is deliberately distinct from an absent client
      // cookie. Wait for useSyncExternalStore to publish the client snapshot
      // before deciding whether a default selection must be persisted.
      if (raw === null) return
      if (!user) return
      if (!teams || teams.length === 0) return

      // Use the centralized resolution logic to determine the correct team
      const resolvedTeamId = resolveActiveTeamId(teams, raw, preferredTeamId)

      if (resolvedTeamId !== null) {
        const alreadyPersisted = raw === resolvedTeamId

        if (alreadyPersisted) {
          if (!cancelled) initializedRef.current = true
          return
        }

        const persisted = await setSelected(resolvedTeamId, {
          skipPreferenceUpdate: true,
        })
        if (!cancelled && persisted) {
          initializedRef.current = true
        }
        return
      }

      const alreadyPersistedSolo = raw === 'solo'
      const personalWorkspaceId = getPersonalWorkspaceMembership(teams)?.value

      if (
        alreadyPersistedSolo &&
        (!personalWorkspaceId || preferredTeamId === personalWorkspaceId)
      ) {
        if (!cancelled) initializedRef.current = true
        return
      }

      const persisted = await setSolo()
      if (!cancelled && persisted) {
        initializedRef.current = true
      }
    }

    void initialize()

    return () => {
      cancelled = true
    }
  }, [preferredTeamId, user, teams, raw, setSelected, setSolo])

  return {
    selectedTeamId: resolvedIsSolo ? null : resolvedTeamId,
    isSolo: resolvedIsSolo,
    setSelectedTeam: setSelected,
    setSolo,
    clear,
    raw,
  }
}
